/**
 * 2026-09-22 — Tek sağlayıcı kataloğu (etiket + yönetim sayfası yolu).
 *
 * The overview's provider list comes from the SERVER (registered bundles), so a
 * newly enabled provider shows up without a client release. This catalogue only
 * supplies the operator label and the control-plane path; an id it does not
 * know falls back to its raw id and has no page (callers render plain text).
 *
 * Labels and paths mirror shell/sidebar.tsx (SECTIONS) and the routes in
 * App.tsx; keep them in sync when a provider is added.
 */

export type ProviderFamily = 'alias' | 'mailbox';

export interface ProviderCatalogEntry {
  readonly id: string;
  readonly label: string;
  readonly family: ProviderFamily;
  /** SPA path of the provider's control plane (router basename '/admin'). */
  readonly path: string;
  /** Reads mail through the residential SOCKS5 IMAP gateway. */
  readonly residentialGateway: boolean;
  /**
   * Static FAIL-SAFE only (D-246; 2026-10-23 D-263 / operator D13). The sidebar
   * strike and the GMX mailbox page's read-only mode come from the SERVER: the
   * overview's per-provider `passive` / `salesClosed` (see resolveProviderLine).
   * This value is used only until the overview has loaded, when it cannot be
   * read (error, no provider:view_health) or when the server does not list the
   * provider. It records the steady state after the 2026-10-23 switch: the GMX
   * alias line (gmx) sells again and the GMX mailbox line (gmxbox) is passive.
   */
  readonly passive?: boolean;
}

export const PROVIDER_CATALOG: readonly ProviderCatalogEntry[] = [
  { id: 'mailcom', label: 'mail.com', family: 'alias', path: '/mailcom-control-plane', residentialGateway: false },
  // 2026-10-23 (D-263): the GMX alias line is back on sale (the mail.com model);
  // live/passive is server-driven, this entry is only the fail-safe.
  { id: 'gmx', label: 'gmx.com', family: 'alias', path: '/gmx-control-plane', residentialGateway: false },
  // D-247: GMX Mailbox — FIRST entry of the Mailbox family. Reads GMX over the
  // shared saved residential gateway (D-245), edited on the t-online page.
  // 2026-10-23 (D-263): its accounts moved to the gmx alias line; the line stays
  // registered but passive/read-only (fail-safe value; the server decides).
  { id: 'gmxbox', label: 'GMX', family: 'mailbox', path: '/gmxbox-control-plane', residentialGateway: true, passive: true },
  { id: 'rambler', label: 'rambler.ru', family: 'mailbox', path: '/rambler-control-plane', residentialGateway: false },
  { id: 'aol', label: 'aol.com', family: 'mailbox', path: '/aol-control-plane', residentialGateway: false },
  { id: 'libero', label: 'libero.it', family: 'mailbox', path: '/libero-control-plane', residentialGateway: false },
  { id: 'tiscaliit', label: 'tiscali.it', family: 'mailbox', path: '/tiscaliit-control-plane', residentialGateway: false },
  { id: 'tiscalicz', label: 'tiscali.cz', family: 'mailbox', path: '/tiscalicz-control-plane', residentialGateway: true },
  { id: 'sinavip', label: 'vip.sina.com', family: 'mailbox', path: '/sinavip-control-plane', residentialGateway: false },
  { id: 'sina', label: 'sina.com', family: 'mailbox', path: '/sina-control-plane', residentialGateway: false },
  { id: 'gazetapl', label: 'gazeta.pl', family: 'mailbox', path: '/gazetapl-control-plane', residentialGateway: false },
  { id: 'rediffmail', label: 'rediffmail.com', family: 'mailbox', path: '/rediffmail-control-plane', residentialGateway: false },
  { id: 'tonline', label: 't-online.de', family: 'mailbox', path: '/tonline-control-plane', residentialGateway: true },
];

const BY_ID: ReadonlyMap<string, ProviderCatalogEntry> = new Map(PROVIDER_CATALOG.map((e) => [e.id, e]));

/**
 * 2026-10-08 (operator Q96-Q100) — a REMOVED provider keeps only its display
 * label, so retained history (orders, served-once rows, resolved incidents)
 * still shows a name instead of the raw id. It has no catalogue entry: no
 * control-plane path, no sidebar entry, no page (providerPath returns null and
 * callers render the label as plain text).
 */
const HISTORICAL_PROVIDER_LABELS: ReadonlyMap<string, string> = new Map([['webde', 'web.de']]);

export function providerCatalogEntry(id: string | null | undefined): ProviderCatalogEntry | null {
  if (!id) return null;
  return BY_ID.get(id) ?? null;
}

/** Operator label: catalogue label, else the server's label, else the raw id. */
export function providerLabel(id: string | null | undefined, serverLabel?: string | null): string {
  const entry = providerCatalogEntry(id);
  if (entry) return entry.label;
  const historical = id ? HISTORICAL_PROVIDER_LABELS.get(id) : undefined;
  if (historical) return historical;
  if (serverLabel && serverLabel.length > 0) return serverLabel;
  return id ?? '—';
}

/** D-246 — true when the catalogue marks the provider line passive (static fail-safe). */
export function providerIsPassive(id: string | null | undefined): boolean {
  return providerCatalogEntry(id)?.passive === true;
}

/**
 * The server's view of one provider line, as carried by the admin overview
 * (`GET /admin/overview` → providers[]): `passive` (D-246: sales + renewal off,
 * read-only) and `salesClosed` (2026-10-23: the provider's sales flag is off or
 * it has no active catalogue row). Structural, so an OverviewProvider fits.
 */
export interface ServerProviderLine {
  readonly providerId: string;
  readonly passive?: boolean;
  readonly salesClosed?: boolean;
}

export type ProviderLineReason = 'live' | 'passive' | 'sales_closed';

export interface ProviderLineStatus {
  /** Struck through in the sidebar (passive OR sales-closed). */
  readonly struck: boolean;
  readonly reason: ProviderLineReason;
  /** 'server' = read from the overview; 'catalog' = static fail-safe. */
  readonly source: 'server' | 'catalog';
}

/**
 * 2026-10-23 (operator D13) — a provider line is struck when the SERVER says
 * it is passive OR sales-closed. `serverLines` is the overview's provider list,
 * or null while it has not loaded / cannot be read; then (and for an id the
 * server does not list) the static catalogue value is the fail-safe.
 */
export function resolveProviderLine(
  id: string | null | undefined,
  serverLines: readonly ServerProviderLine[] | null | undefined,
): ProviderLineStatus {
  const server = id && serverLines ? serverLines.find((p) => p.providerId === id) : undefined;
  if (server) {
    if (server.passive === true) return { struck: true, reason: 'passive', source: 'server' };
    if (server.salesClosed === true) return { struck: true, reason: 'sales_closed', source: 'server' };
    return { struck: false, reason: 'live', source: 'server' };
  }
  return providerIsPassive(id)
    ? { struck: true, reason: 'passive', source: 'catalog' }
    : { struck: false, reason: 'live', source: 'catalog' };
}

/**
 * Control-plane path, or null for an id outside PROVIDER_CATALOG. 2026-10-23:
 * the generic /providers/:id "Sağlayıcı Sağlığı" panel page was removed, so an
 * unknown or historical id has no page; callers render plain text (no link)
 * instead of a link that would fall through to the dashboard.
 */
export function providerPath(id: string | null | undefined): string | null {
  return providerCatalogEntry(id)?.path ?? null;
}

/** Comma-joined labels for a list (or CSV string) of provider ids. */
export function providerLabels(ids: readonly string[] | string | null | undefined): string {
  const list = typeof ids === 'string' ? ids.split(',') : ids ?? [];
  return list
    .map((s) => s.trim())
    .filter((s) => s.length > 0)
    .map((s) => providerLabel(s))
    .join(', ');
}
